import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Text, Flex, BetaBadge } from 'Components/Base';
import PresentationModal from 'Components/PresentationModal/PresentationModal';

import step1_1x from '../../../../../public/assets/images/guidedPractice/tutorial-1@1x.png';
import step1_2x from '../../../../../public/assets/images/guidedPractice/tutorial-1@2x.png';
import step1_4x from '../../../../../public/assets/images/guidedPractice/tutorial-1@3x.png';
import step2 from '../../../../../public/assets/images/guidedPractice/tutorial-2.json';
import step3_1x from '../../../../../public/assets/images/guidedPractice/tutorial-3@1x.png';
import step3_2x from '../../../../../public/assets/images/guidedPractice/tutorial-3@2x.png';
import step3_4x from '../../../../../public/assets/images/guidedPractice/tutorial-3@3x.png';

const StyledContentText = styled((props) => <Text textStyle="body1" {...props} />)`
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: ${({ theme }) => theme.lineHeights.normal};
`;

const StyledContentTitle = styled((props) => <Text textStyle="heading4" mb={4} {...props} />)`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const StyledOrderedList = styled.ol`
  padding-inline-start: 24px;
  margin: 0;
`;

const contents = [
  {
    visual: {
      src: step1_1x,
      srcSet: `${step1_1x} 1x, ${step1_2x} 2x, ${step1_4x} 4x`,
    },
    buttonText: 'ไปต่อ',
    content: (
      <>
        <StyledContentTitle>
          เรียนแล้วไม่ลืมเนื้อหาสำคัญ
          <br />
          ด้วยฟีเจอร์ใหม่ Guided Practice !
        </StyledContentTitle>
        <StyledContentText>
          งานวิจัยชี้ชัดว่า <u>เรามักลืมความรู้ไปกว่า 75% ภายใน 7 วัน</u> หลังเรียน หากไม่ทบทวนเลย!
        </StyledContentText>
        <br />
        <StyledContentText>
          <Text fontWeight="600">Guided Practice</Text>
          คือระบบฝึกที่จะช่วยให้น้องได้ทบทวน ความรู้ในจังหวะเวลา ที่เหมาะสมน้องจะสามารถ
          <br />
          ✅ จำเนื้อหาสำคัญได้นานขึ้น
          <br />
          ✅ พร้อมต่อยอดสู่การฝึกทำโจทย์ข้อสอบจริง
          <br />✅ เตรียมตัวสอบ A-Level อย่างมั่นใจ
        </StyledContentText>
      </>
    ),
  },
  {
    visual: step2,
    buttonText: 'ไปต่อ',
    content: (
      <>
        <StyledContentTitle>เริ่มต้นใช้งาน Guided Practice</StyledContentTitle>
        <StyledContentText>
          ง่ายๆ เพียงน้องเรียนจบเนื้อหาที่กำหนดครบ 7 วัน ระบบ จะทำการส่ง “แบบฝึกทบทวน” ให้ โดยน้องๆ
          สามารถเริ่ม ทำแบบฝึกทบทวน ได้ตามขั้นตอนดังนี้
        </StyledContentText>
        <br />
        <StyledContentText>
          <Text fontWeight="500">ขั้นตอนเริ่มทำแบบฝึกทบทวน</Text>
          <StyledOrderedList>
            <li>เลือกแบบฝึกทบทวนที่มีสถานะ “รอทบทวน”</li>
            <li>เข้าสู่หน้า “แนะนำแบบฝึกทบทวน” ที่อธิบายถึงภาพรวม ของแบบฝึกทบทวน</li>
            <li>กด “เริ่มเลย” เพื่อเริ่มต้นทำแบบฝึกทบทวน</li>
          </StyledOrderedList>
        </StyledContentText>
      </>
    ),
  },
  {
    visual: {
      src: step3_1x,
      srcSet: `${step3_1x} 1x, ${step3_2x} 2x, ${step3_4x} 4x`,
    },
    buttonText: 'เริ่มเลย',
    content: (
      <>
        <StyledContentTitle>💡 ข้อควรรู้ ก่อนทบทวน</StyledContentTitle>
        <StyledContentText>
          <StyledOrderedList>
            <li>แบบฝึกทบทวนแต่ละชุด สามารถทำได้ครั้งเดียวเท่านั้น</li>
            <li>
              ตั้งใจอ่าน “บทความและเฉลย” ที่มีให้ เพื่อทบทวนความ เข้าใจจากเนื้อหาที่เคยเรียนมา
            </li>
            <li>
              “ผิดหรือถูก” ไม่สำคัญ สิ่งสำคัญคือ การได้กลับมา ทบทวนความรู้ เพื่อมาวิเคราะห์ตนเอง
              และจุดอ่อน เพื่อแก้ไขให้แข็งแกร่ง 💪 ก่อนเจอสนามจริง
            </li>
          </StyledOrderedList>
        </StyledContentText>
        <br />
        <Text textStyle="body1" fontWeight="500">
          มาลองใช้ Guided Practice กันครับ !
        </Text>
      </>
    ),
  },
];

const HeaderWrapper = styled(Flex)`
  column-gap: 16px;
  row-gap: 8px;
`;

const Header = () => {
  return (
    <HeaderWrapper
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      flexDirection={{ xs: 'column', sm: 'row' }}
    >
      <Text textStyle="heading5" fontWeight="500">
        ยินดีต้อนรับสู่ Guided Practice 🎉
      </Text>
    </HeaderWrapper>
  );
};

const GuidedPracticeTutorialModal = ({ onClose, open }) => {
  const contentsMemo = React.useMemo(() => contents, []);
  return (
    <PresentationModal title={<Header />} contents={contentsMemo} onClose={onClose} open={open} />
  );
};

GuidedPracticeTutorialModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default GuidedPracticeTutorialModal;
